import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, IconButton, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import iziToast from 'izitoast';

import JournalLayout from '../../components/Journal/JournalLayout';
import useIsMobile from '../../utils/useIsMobile';
import { getWorkload } from '../../actions/journal';
import { internalServerError, journalSheetLink } from '../../utils/consts/pathRoutes';
import { currentMonth, formatDate, formatMonth, shiftMonth } from '../../utils/journalFormat';
import { Link } from 'react-router-dom';
import './sheet.css';

/**
 * Выработка часов преподавателей за месяц.
 *
 * Отчёт, ради которого заказчик готов отказаться от цветовой раскраски: он показывает,
 * сколько часов преподаватель действительно провёл — основание для начисления зарплаты.
 *
 * Часы считаются по занятиям, а не по отметкам слушателей: пара проведена независимо
 * от того, сколько человек на неё пришло.
 */
const WorkloadPage = () => {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    setLoading(true);
    getWorkload(month)
      .then(setData)
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setLoading(false));
  }, [month]);

  /**
   * Преподаватель на телефоне — карточкой. Списки восстановленных и незаполненных пар,
   * которые в таблице спрятаны в подсказки, здесь выписаны текстом: навести на них
   * на телефоне нечем.
   */
  const renderCards = () => (
    <div className="journal-cards">
      {data.teachers.map((teacher) => (
        <Paper key={teacher.teacher_id} variant="outlined" className="journal-card">
          <div className="journal-card__head">
            <div className="journal-card__title">{teacher.teacher_name}</div>
            <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <b style={{ fontSize: 17 }}>{teacher.hours_total} ч</b>
              <div className="journal-muted">занятий: {teacher.lessons_count}</div>
            </div>
          </div>

          <div className="journal-card__row">
            {teacher.groups.map((group) => (
              <Link key={group.group_id} to={journalSheetLink(group.group_id)}>
                <Chip size="small" clickable label={`${group.name}: ${group.hours} ч`} />
              </Link>
            ))}
          </div>

          {teacher.restored_hours > 0 && (
            <div className="journal-card__meta">
              Восстановленные: <b>{teacher.restored_hours} ч</b>
              <div className="journal-muted">
                {teacher.restored_lessons
                  .map((lesson) => `${formatDate(lesson.date)} за ${formatDate(lesson.restored_for_date) || '—'}`)
                  .join('; ')}
              </div>
            </div>
          )}

          {teacher.unfilled_lessons.length > 0 && (
            <div className="journal-card__meta">
              <Chip
                size="small" color="warning" variant="outlined"
                label={`Не заполнено: ${teacher.unfilled_lessons.length}`}
              />
              <div className="journal-muted" style={{ marginTop: 4 }}>
                {teacher.unfilled_lessons
                  .map((lesson) => `${formatDate(lesson.date)} ${lesson.group}`)
                  .join('; ')}
              </div>
            </div>
          )}
        </Paper>
      ))}
    </div>
  );

  return (
    <JournalLayout activeTab="workload">
      <Box>
        <Stack
          direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}
          {...(isMobile && { spacing: 0, sx: { mb: 2, flexWrap: 'wrap', columnGap: 1 } })}
        >
          <h2 className="journal-title">Выработка часов</h2>
          {/* На телефоне отступ становится переносом: месяц — отдельной строкой. */}
          <Box sx={isMobile ? { flexBasis: '100%', height: 8 } : { width: 16 }} />
          <IconButton size="small" onClick={() => setMonth(shiftMonth(month, -1))}>
            <ChevronLeftIcon />
          </IconButton>
          <span className="sheet__month">{formatMonth(month)}</span>
          <IconButton size="small" onClick={() => setMonth(shiftMonth(month, 1))}>
            <ChevronRightIcon />
          </IconButton>
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress color="warning" /></Stack>
        ) : !data?.teachers?.length ? (
          <Paper sx={{ p: 4, textAlign: 'center' }} className="journal-muted">
            За {formatMonth(month)} проведённых занятий нет.
            Часы считаются по занятиям, у которых указан преподаватель.
          </Paper>
        ) : (
          <>
            <Stack direction="row" spacing={3} sx={{ mb: 2, flexWrap: 'wrap' }}>
              <span>Всего часов: <b>{data.total.hours}</b></span>
              <span className="journal-muted">занятий: {data.total.lessons}</span>
              {data.total.restored_hours > 0 && (
                <span className="journal-muted">
                  из них восстановленных: {data.total.restored_hours} ч
                </span>
              )}
            </Stack>

            {data.total.unfilled > 0 && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2, maxWidth: 900 }}>
                Прошедших занятий без единой отметки: {data.total.unfilled}.
                Часы преподавателю засчитаны, но посещаемость не проставлена.
              </Alert>
            )}

            {isMobile ? renderCards() : (
              <Paper>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Преподаватель</TableCell>
                      <TableCell align="center">Часов</TableCell>
                      <TableCell align="center">Занятий</TableCell>
                      <TableCell>По группам</TableCell>
                      <TableCell>Восстановленные</TableCell>
                      <TableCell>Не заполнено</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.teachers.map((teacher) => (
                      <TableRow key={teacher.teacher_id} hover>
                        <TableCell><b>{teacher.teacher_name}</b></TableCell>
                        <TableCell align="center" sx={{ fontSize: 15 }}>
                          <b>{teacher.hours_total}</b>
                        </TableCell>
                        <TableCell align="center">{teacher.lessons_count}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                            {teacher.groups.map((group) => (
                              <Link key={group.group_id} to={journalSheetLink(group.group_id)}>
                                <Chip
                                  size="small" clickable
                                  label={`${group.name}: ${group.hours} ч`}
                                />
                              </Link>
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {teacher.restored_hours === 0 ? '—' : (
                            <Tooltip title={teacher.restored_lessons
                              .map((lesson) => `${formatDate(lesson.date)} за ${formatDate(lesson.restored_for_date) || '—'}`)
                              .join('; ')}
                            >
                              <span>{teacher.restored_hours} ч</span>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          {teacher.unfilled_lessons.length === 0 ? '—' : (
                            <Tooltip title={teacher.unfilled_lessons
                              .map((lesson) => `${formatDate(lesson.date)} ${lesson.group}`)
                              .join('; ')}
                            >
                              <Chip
                                size="small" color="warning" variant="outlined"
                                label={teacher.unfilled_lessons.length}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </>
        )}
      </Box>
    </JournalLayout>
  );
};

export default WorkloadPage;
